/**
 * Error tracking · 2026-05-20 — Cloudflare-native error reporter.
 *
 * History:
 *   - Original Production launch · 2026-05-20 wired this module to
 *     Sentry's envelope endpoint. We've since rebuilt error tracking
 *     INSIDE Cloudflare (Worker → D1 events → AdminObservability +
 *     Slack webhook), so this bridge now POSTs to the Builder API
 *     by default. Sentry envelope upload is kept as an OPT-IN
 *     secondary channel when `VITE_SENTRY_DSN` is set — useful if an
 *     operator later wants both real-time Slack alerts + Sentry's
 *     dedupe view.
 *
 * Public API (callers MUST keep working):
 *   - initSentry(opts?)           — call once at startup, idempotent.
 *   - isSentryConfigured()        — true when REAL Sentry DSN parsed
 *                                   ok (does NOT report on the Builder
 *                                   channel — that's always on).
 *   - captureExceptionToSentry()  — fire-and-forget capture. Always
 *                                   posts to Builder API. ALSO posts
 *                                   to Sentry when configured.
 *   - captureException()          — alias of captureExceptionToSentry
 *                                   for callers that want the
 *                                   conventional name.
 *   - captureMessage()            — capture a string with synthetic
 *                                   stack. Convenience wrapper.
 *   - flushOfflineBuffer()        — drain the localStorage buffer
 *                                   when the network is back.
 *
 * Offline buffer:
 *   When fetch fails (offline laptop, captive portal), the report is
 *   queued in `localStorage.builder.observability.buffer` as a JSON
 *   array, max 50 entries (oldest evicted). Whenever the bridge
 *   captures a new error, it tries to drain the buffer first; we also
 *   wire up a `window.online` listener at init.
 *
 * Sampling:
 *   sample rate defaults to 1.0 (capture everything). Set
 *   `opts.sampleRate` between 0 and 1 to drop a fraction client-side
 *   if volume grows beyond Slack/D1 comfort. See
 *   `docs/ERROR-TRACKING-CF.md` for tuning guidance.
 *
 * Privacy:
 *   PII scrubbing identical to apps/api/src/lib/observability.ts —
 *   emails, JWTs, Bearer tokens get replaced before the report leaves
 *   the browser. The Worker also re-scrubs on the way in (defense in
 *   depth).
 */

interface ParsedDsn {
  publicKey: string;
  envelopeUrl: string;
}

function parseSentryDsn(dsn: string): ParsedDsn | null {
  try {
    const u = new URL(dsn);
    const publicKey = u.username;
    const projectId = u.pathname.replace(/^\//, '').replace(/\/$/, '');
    if (!publicKey || !projectId) return null;
    const envelopeUrl = `${u.protocol}//${u.host}/api/${projectId}/envelope/`;
    return { publicKey, envelopeUrl };
  } catch {
    return null;
  }
}

const SCRUB_RE = [
  { re: /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, with: '[REDACTED_EMAIL]' },
  { re: /eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g, with: '[REDACTED_JWT]' },
  { re: /Bearer\s+[A-Za-z0-9._-]+/gi, with: 'Bearer [REDACTED]' },
];

function scrubString(s: string): string {
  if (!s) return s;
  let out = s;
  for (const { re, with: w } of SCRUB_RE) out = out.replace(re, w);
  return out;
}

let cachedDsn: ParsedDsn | null = null;
let cachedEnvironment = 'production';
let cachedRelease: string | undefined;
let cachedSampleRate = 1.0;
let cachedApiBase = '';
let onlineListenerInstalled = false;

const BUFFER_KEY = 'builder.observability.buffer';
const BUFFER_MAX = 50;

export interface CaptureContext {
  feature?: string;
  level?: 'app' | 'route' | 'feature' | 'global';
  route?: string;
  userId?: string;
  buildId?: string;
  /** Severity hint — server clamps to known set. */
  severity?: 'low' | 'medium' | 'high' | 'critical';
  sessionId?: string;
  role?: string;
}

export interface InitOptions {
  dsn?: string;
  environment?: string;
  release?: string;
  /** 0..1; defaults to 1.0 (capture all). */
  sampleRate?: number;
  /** Override the API base used to POST builder reports (tests). */
  apiBase?: string;
}

/**
 * Initialise the bridge. Call once from main.tsx. Idempotent — repeat
 * calls just update the cached config.
 */
export function initSentry(opts?: InitOptions): void {
  // Sentry DSN: optional secondary channel.
  const dsn =
    opts?.dsn ??
    (import.meta as unknown as { env?: { VITE_SENTRY_DSN?: string } }).env?.VITE_SENTRY_DSN;
  cachedDsn = dsn ? parseSentryDsn(dsn) : null;
  if (opts?.environment) cachedEnvironment = opts.environment;
  if (opts?.release) cachedRelease = opts.release;
  if (typeof opts?.sampleRate === 'number') {
    cachedSampleRate = Math.max(0, Math.min(1, opts.sampleRate));
  }
  if (typeof opts?.apiBase === 'string') {
    cachedApiBase = opts.apiBase;
  } else {
    cachedApiBase =
      (import.meta as unknown as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE ?? '';
  }
  installOnlineListener();
}

/** True when REAL Sentry envelope endpoint is configured. */
export function isSentryConfigured(): boolean {
  return cachedDsn !== null;
}

function installOnlineListener(): void {
  if (onlineListenerInstalled) return;
  if (typeof window === 'undefined' || !window.addEventListener) return;
  onlineListenerInstalled = true;
  window.addEventListener('online', () => {
    void flushOfflineBuffer();
  });
}

// ── builder API channel ───────────────────────────────────────────────

interface QueuedReport {
  error: { message: string; stack?: string; type: string };
  context: {
    url: string | null;
    userAgent: string | null;
    role: string | null;
    sessionId: string | null;
    feature: string | null;
    buildId: string | null;
    level: string | null;
  };
  severity: 'low' | 'medium' | 'high' | 'critical';
  queuedAt: string;
}

function readBuffer(): QueuedReport[] {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed as QueuedReport[];
  } catch {
    return [];
  }
}

function writeBuffer(reports: QueuedReport[]): void {
  try {
    // Cap to BUFFER_MAX, evicting the oldest entries first.
    const trimmed = reports.slice(-BUFFER_MAX);
    localStorage.setItem(BUFFER_KEY, JSON.stringify(trimmed));
  } catch {
    /* localStorage unavailable — drop silently */
  }
}

function appendToBuffer(report: QueuedReport): void {
  const buf = readBuffer();
  buf.push(report);
  writeBuffer(buf);
}

async function postReportToBuilder(report: QueuedReport): Promise<boolean> {
  if (typeof fetch === 'undefined') return false;
  let authToken: string | null = null;
  try {
    if (typeof localStorage !== 'undefined') {
      authToken = localStorage.getItem('builder.auth.token');
    }
  } catch {
    authToken = null;
  }
  const url = `${cachedApiBase}/v1/observability/client-error`;
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(authToken ? { authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify(report),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * Drain the offline buffer. Called on `online` event and at the start
 * of every capture. Errors stop the drain but don't throw — partial
 * progress is fine, the rest stays in the buffer for the next try.
 */
export async function flushOfflineBuffer(): Promise<{ flushed: number; remaining: number }> {
  const buf = readBuffer();
  if (buf.length === 0) return { flushed: 0, remaining: 0 };
  let flushed = 0;
  const remaining: QueuedReport[] = [];
  for (const report of buf) {
    if (flushed === buf.length) break;
    const ok = await postReportToBuilder(report);
    if (ok) {
      flushed += 1;
    } else {
      // Stop on first failure — we're probably still offline.
      remaining.push(report);
    }
  }
  // Anything we didn't try yet stays queued.
  if (flushed < buf.length) {
    for (let i = flushed; i < buf.length; i++) {
      const entry = buf[i];
      if (entry) remaining.push(entry);
    }
  }
  // Dedup: if all succeeded, write empty; otherwise write what's left.
  if (flushed === buf.length) {
    writeBuffer([]);
  } else {
    writeBuffer(remaining);
  }
  return { flushed, remaining: remaining.length };
}

// ── public capture API ────────────────────────────────────────────────

/**
 * Fire-and-forget capture. Never throws — a broken observability pipe
 * must not crash the recovery UI. Returns true when the report was
 * POSTed to the Builder API successfully (Sentry secondary channel is
 * best-effort and does not affect the return value).
 */
export async function captureExceptionToSentry(
  error: unknown,
  context?: CaptureContext,
): Promise<boolean> {
  // Sampling — drop a fraction client-side if configured.
  if (cachedSampleRate < 1 && Math.random() > cachedSampleRate) {
    return false;
  }

  const errType = error instanceof Error ? error.name || 'Error' : 'Unknown';
  const errMsgRaw = error instanceof Error ? error.message : String(error ?? '');
  const errMsg = scrubString(errMsgRaw);
  const errStackRaw = error instanceof Error ? error.stack : undefined;
  const errStack = errStackRaw ? scrubString(errStackRaw) : undefined;

  const report: QueuedReport = {
    error: { message: errMsg, stack: errStack, type: errType },
    context: {
      url: typeof window !== 'undefined' ? window.location.href : null,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      role: context?.role ?? null,
      sessionId: context?.sessionId ?? null,
      feature: context?.feature ?? null,
      buildId: context?.buildId ?? cachedRelease ?? null,
      level: context?.level ?? null,
    },
    severity: context?.severity ?? 'medium',
    queuedAt: new Date().toISOString(),
  };

  // Try drain anything queued before this report. Fire-and-forget — we
  // don't want the new report to wait on a slow drain.
  void flushOfflineBuffer();

  // Best-effort online check. `navigator.onLine` is unreliable but a
  // good first filter — if it's explicitly false, skip the fetch and
  // buffer immediately.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    appendToBuffer(report);
    return false;
  }

  const ok = await postReportToBuilder(report);
  if (!ok) {
    appendToBuffer(report);
  }

  // Secondary channel: when a real Sentry DSN is configured, also POST
  // an envelope. We don't block the primary return on this — best
  // effort, errors swallowed.
  if (cachedDsn) {
    void postSentryEnvelope(error, context, errMsg, errStack);
  }

  return ok;
}

/** Conventional alias — same behavior as captureExceptionToSentry. */
export const captureException = captureExceptionToSentry;

/** Capture a free-form string. Builds a synthetic Error for stack. */
export async function captureMessage(
  message: string,
  context?: CaptureContext,
): Promise<boolean> {
  const synth = new Error(message);
  return captureExceptionToSentry(synth, context);
}

// ── secondary Sentry envelope channel (opt-in) ────────────────────────

async function postSentryEnvelope(
  error: unknown,
  context: CaptureContext | undefined,
  errMsg: string,
  errStack: string | undefined,
): Promise<boolean> {
  if (!cachedDsn) return false;
  try {
    const eventId = randomEventId();
    const timestamp = Math.floor(Date.now() / 1000);

    const errType = error instanceof Error ? error.name || 'Error' : 'Unknown';

    const event = {
      event_id: eventId,
      timestamp,
      level: 'error',
      platform: 'javascript',
      environment: cachedEnvironment,
      release: cachedRelease ?? context?.buildId,
      server_name: 'builder-web',
      sdk: { name: 'builder-web', version: '1.0.0' },
      tags: {
        ...(context?.feature ? { feature: context.feature } : {}),
        ...(context?.level ? { boundary_level: context.level } : {}),
        ...(context?.route ? { route: context.route } : {}),
      },
      user: context?.userId ? { id: context.userId } : undefined,
      exception: {
        values: [
          {
            type: errType,
            value: errMsg,
            ...(errStack ? { stacktrace: { frames: parseStackFrames(errStack) } } : {}),
          },
        ],
      },
    };

    const header = JSON.stringify({
      event_id: eventId,
      sent_at: new Date().toISOString(),
      dsn: undefined,
    });
    const itemHeader = JSON.stringify({ type: 'event', content_type: 'application/json' });
    const envelope = `${header}\n${itemHeader}\n${JSON.stringify(event)}`;

    const res = await fetch(cachedDsn.envelopeUrl, {
      method: 'POST',
      headers: {
        'content-type': 'application/x-sentry-envelope',
        'x-sentry-auth': `Sentry sentry_version=7, sentry_key=${cachedDsn.publicKey}, sentry_client=builder-web/1.0`,
      },
      body: envelope,
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

function parseStackFrames(
  stack: string,
): Array<{ filename?: string; function?: string; lineno?: number }> {
  const lines = stack.split('\n').slice(1, 31);
  const frames: Array<{ filename?: string; function?: string; lineno?: number }> = [];
  for (const line of lines) {
    const m =
      /at\s+(?<fn>[^\s(]+)\s+\((?<file>[^)]+?):(?<ln>\d+):\d+\)/.exec(line) ??
      /at\s+(?<file>[^\s(]+?):(?<ln>\d+):\d+/.exec(line);
    if (!m) continue;
    frames.push({
      function: m.groups?.fn,
      filename: m.groups?.file,
      lineno: m.groups?.ln ? Number(m.groups.ln) : undefined,
    });
  }
  return frames.reverse();
}

function randomEventId(): string {
  // 32 hex chars no dashes — Sentry's expected event_id format.
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

// Test seam — reset cached config between tests.
export function __resetSentryForTests(): void {
  cachedDsn = null;
  cachedEnvironment = 'production';
  cachedRelease = undefined;
  cachedSampleRate = 1.0;
  cachedApiBase = '';
  onlineListenerInstalled = false;
  try {
    localStorage.removeItem(BUFFER_KEY);
  } catch {
    /* nothing to do */
  }
}

/** Read-only access to the parsed DSN for tests / debug surface. */
export function __getSentryConfigForTests(): {
  dsn: ParsedDsn | null;
  environment: string;
  release: string | undefined;
  sampleRate: number;
  apiBase: string;
} {
  return {
    dsn: cachedDsn,
    environment: cachedEnvironment,
    release: cachedRelease,
    sampleRate: cachedSampleRate,
    apiBase: cachedApiBase,
  };
}

/** Test seam — read the offline buffer. */
export function __readBufferForTests(): QueuedReport[] {
  return readBuffer();
}
